import { useCallback, useEffect, useState } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetCurrentBalanceUseCase } from "../../domain/use-cases/GetCurrentBalanceUseCase";
import {
  formatPlAmount,
  formatPlChangePercent,
  niceMaxAmount,
  scaleToBarHeight,
} from "../utils/formatPlAmount";

const repository = new ClientOverviewRepositoryImpl();
const getCurrentBalanceUseCase = new GetCurrentBalanceUseCase(repository);

export interface CurrentBalanceCardViewModel {
  asOfLabel: string;
  balanceAmount: string;
  balanceSubtitle: string;
  barHeights: number[];
  barLabels: string[];
  barAmounts: string[];
  momPercentLabel: string;
  runwayLabel: string;
}

export function useCurrentBalance(): {
  card: CurrentBalanceCardViewModel | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [card, setCard] = useState<CurrentBalanceCardViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrentBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const entity = await getCurrentBalanceUseCase.execute();
      const labels = entity.chart?.labels ?? [];
      const values = entity.chart?.values ?? [];

      const maxValue = niceMaxAmount(
        values.length > 0 ? Math.max(...values, 0) : 0,
      );

      const barHeights = values.map((v) => scaleToBarHeight(v, maxValue));
      const barAmounts = values.map((v) => formatPlAmount(v));

      const runwayLabel =
        entity.runwayMonths != null
          ? `${entity.runwayMonths} mo. runway`
          : "—";

      const momPercentLabel =
        entity.momPercent != null
          ? `${formatPlChangePercent(Number(entity.momPercent.toFixed(1)))} from previous closed month`
          : "—";

      const formattedBalance =
        entity.balanceAmountRaw != null
          ? formatPlAmount(entity.balanceAmountRaw)
          : entity.balanceAmount;

      setCard({
        asOfLabel: entity.asOfLabel,
        balanceAmount: formattedBalance,
        balanceSubtitle: entity.balanceSubtitle,
        barHeights,
        barLabels: labels,
        barAmounts,
        momPercentLabel,
        runwayLabel,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load current balance";
      setError(message);
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentBalance();
  }, [fetchCurrentBalance]);

  return { card, loading, error, refetch: fetchCurrentBalance };
}

